// Cloudinary delivery URL builders. Transformations copied verbatim from
// specs/plan.md's "Cloudinary" section. The cloud name comes from
// GET /api/config (see lib/api.ts's `config()`).
import type { Asset } from "./types"

const BASE = "https://res.cloudinary.com"

export function thumbUrl(cloudName: string, publicId: string): string {
	return `${BASE}/${cloudName}/image/upload/c_fill,w_500,h_500,f_auto,q_auto/${publicId}`
}

/** The stored asset with no transformations — used as the source when
 *  re-editing an uploaded photo, so edits start from the best pixels we
 *  have. Cloudinary serves delivery URLs with CORS headers, so the client
 *  can fetch() this into a Blob. */
export function originalUrl(cloudName: string, publicId: string): string {
	return `${BASE}/${cloudName}/image/upload/${publicId}`
}

// The detail view is at most 720 CSS px wide, so a 1600px render is wasted
// bytes on anything but a high-DPR phone. Handing the browser a srcset lets
// it pick the smallest render that still looks sharp on that screen; fullUrl
// stays as the widest render for browsers that ignore srcset.
const FULL_WIDTHS = [800, 1200, 1600]

function imageUrl(cloudName: string, publicId: string, width: number): string {
	return `${BASE}/${cloudName}/image/upload/w_${width},f_auto,q_auto/${publicId}`
}

export function fullUrl(cloudName: string, publicId: string): string {
	return imageUrl(cloudName, publicId, FULL_WIDTHS[FULL_WIDTHS.length - 1])
}

export function fullSrcSet(cloudName: string, publicId: string): string {
	return FULL_WIDTHS.map(
		(w) => `${imageUrl(cloudName, publicId, w)} ${w}w`,
	).join(", ")
}

// Recordings are stored in their delivery form (720p h264 mp4 / mp3) by an
// incoming transformation at upload time, so these URLs ask for the stored
// asset itself and Cloudinary has nothing to transcode. Keep them in step
// with internal/server/cloudinary.go's storage constants — that file's
// TestStoredFormatsMatchDelivery checks this one.
export function videoMp4Url(cloudName: string, publicId: string): string {
	return `${BASE}/${cloudName}/video/upload/${publicId}.mp4`
}

/** Still frame from the start of a video, used as the <video> poster so the
 *  player shows something without downloading the video itself. */
export function videoPosterUrl(cloudName: string, publicId: string): string {
	return `${BASE}/${cloudName}/video/upload/so_0/${publicId}.jpg`
}

export function audioMp3Url(cloudName: string, publicId: string): string {
	return `${BASE}/${cloudName}/video/upload/${publicId}.mp3`
}

/** A recording asset is audio-only when Cloudinary reports no width. */
export function isAudioOnly(asset: Pick<Asset, "width">): boolean {
	return asset.width === 0
}

/** Picks the audio or video delivery URL for a "recording" asset based on
 *  the width==0 rule from specs/plan.md. */
export function recordingUrl(
	cloudName: string,
	asset: Pick<Asset, "cloudinary_public_id" | "width">,
): string {
	return isAudioOnly(asset)
		? audioMp3Url(cloudName, asset.cloudinary_public_id)
		: videoMp4Url(cloudName, asset.cloudinary_public_id)
}
